import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { alertError, alertSuccess } from '../../../components/Toast';
import { ENTRY_TYPE, INSTALLMENT_STATUS } from '../../../core/constants/financial';
import {
  deleteFinancialEntry,
  loadFinancialEntriesByPeriodAndType,
} from '../../../core/utils/financialEntry';
import { softDeleteBreakdownByEntry } from '../../../core/utils/breakdown';
import { getRangeFromFilter, todayISO } from '../../../core/utils/financialPeriod';
import { formatCurrency, formatDateBR } from '../../../core/utils/format';
import { useTableSort } from '../../../core/hooks/useTableSort';
import SortableTh from './SortableTh';
import FinancialEntryModal from './FinancialEntryModal';
import FinancialListModal from './FinancialListModal';
import PeriodFilter from './PeriodFilter';

const typeBadgeStyle = (type) =>
  type === ENTRY_TYPE.RECEITA
    ? { background: 'rgba(91,196,36,0.15)', color: '#70E000' }
    : { background: 'rgba(232,140,42,0.15)', color: '#e88c2a' };

const SORT_ACCESSORS = {
  emissao: (e) => e.issue_date || '',
  tipo: (e) => e.type || '',
  parte: (e) => e.supplier_client || '',
  plano: (e) => e.AccountPlan?.name || '',
  valor: (e) => Number(e.total_value) || 0,
  parcelas: (e) => (e.Installment || []).length,
};

// Lista geral de lançamentos do período (receita + despesa) seguindo o padrão
// dos modais de cadastro (Banco/Plano/Centro):
//   - fd-modal-summary com KPIs (Total / Receitas / Despesas / Resultado)
//   - fd-modal-table--clickable: linha inteira abre edição
//   - Excluir mora no footer do FinancialEntryModal (regra B trava se há
//     parcela paga); confirmação Swal acontece aqui no parent.
const FinancialEntriesListModal = ({ isOpen, onClose, onChanged }) => {
  const company = useSelector((state) => state.app.company || {});
  const user = useSelector((state) => state.app.user || {});

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('todos');
  const [customRange, setCustomRange] = useState({ from: todayISO(), to: todayISO() });

  useEffect(() => {
    if (!isOpen) return;
    setSearch('');
    setFilter('todos');
    setCustomRange({ from: todayISO(), to: todayISO() });
  }, [isOpen]);
  const [refreshKey, setRefreshKey] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [editEntryId, setEditEntryId] = useState(null);

  const range = getRangeFromFilter(filter, customRange);

  useEffect(() => {
    if (!isOpen || !company.id) return;
    let cancelled = false;
    setLoading(true);
    loadFinancialEntriesByPeriodAndType(company.id, range)
      .then((data) => {
        if (cancelled) return;
        setEntries(data || []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, company.id, range.from, range.to, refreshKey]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return entries;
    return entries.filter((e) => {
      const supplier = (e.supplier_client || '').toLowerCase();
      const doc = (e.document || '').toLowerCase();
      const plan = (e.AccountPlan?.name || '').toLowerCase();
      const desc = (e.description || '').toLowerCase();
      return (
        supplier.includes(term) ||
        doc.includes(term) ||
        plan.includes(term) ||
        desc.includes(term)
      );
    });
  }, [entries, search]);

  const totalReceita = filtered
    .filter((e) => e.type === ENTRY_TYPE.RECEITA)
    .reduce((sum, e) => sum + (Number(e.total_value) || 0), 0);
  const totalDespesa = filtered
    .filter((e) => e.type === ENTRY_TYPE.DESPESA)
    .reduce((sum, e) => sum + (Number(e.total_value) || 0), 0);
  const resultado = totalReceita - totalDespesa;

  const { sorted, sort, toggleSort } = useTableSort(filtered, SORT_ACCESSORS);

  const handleNew = () => {
    setEditEntryId(null);
    setCreateOpen(true);
  };

  const handleRowClick = (entry) => {
    setCreateOpen(false);
    setEditEntryId(entry.id);
  };

  const handleEntryClosed = () => {
    setCreateOpen(false);
    setEditEntryId(null);
  };

  const handleEntrySaved = () => {
    setRefreshKey((k) => k + 1);
    if (onChanged) onChanged();
  };

  const handleDelete = async () => {
    const entry = entries.find((e) => e.id === editEntryId);
    if (!entry) return;
    const label = entry.supplier_client || entry.description || 'Lançamento';
    const confirm = await Swal.fire({
      title: 'Excluir lançamento?',
      text: `${label} — ${formatCurrency(entry.total_value)}. Esta ação não pode ser desfeita.`,
      icon: 'warning',
      showCancelButton: true,
      showDenyButton: true,
      showConfirmButton: false,
      denyButtonText: 'Excluir',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
    });
    if (!confirm.isDenied) return;

    const { error } = await deleteFinancialEntry(entry.id, user.id);
    if (error) {
      alertError('Erro ao excluir lançamento.');
      return;
    }
    // Cobrança de OS: remove também a separação da DRE (se houver).
    await softDeleteBreakdownByEntry(entry.id, user.id);
    alertSuccess('Lançamento excluído.');
    handleEntryClosed();
    setRefreshKey((k) => k + 1);
    if (onChanged) onChanged();
  };

  return (
    <>
      <FinancialListModal
        isOpen={isOpen}
        onClose={onClose}
        title="Lançamentos do Período"
        badge={filtered.length}
        searchPlaceholder="Buscar fornecedor/cliente, plano, documento..."
        search={search}
        onSearchChange={setSearch}
        onNew={handleNew}
        newLabel="+ Novo Lançamento"
      >
        <PeriodFilter
          filter={filter}
          onFilterChange={setFilter}
          customRange={customRange}
          onCustomRangeChange={setCustomRange}
        />

        <div className="fd-modal-summary">
          <div className="fd-modal-summary-item">
            <span className="fd-modal-summary-label" style={{ color: '#60b4f6' }}>
              Total Lançamentos
            </span>
            <span className="fd-modal-summary-value" style={{ color: '#60b4f6' }}>
              {filtered.length}
            </span>
          </div>
          <div className="fd-modal-summary-item">
            <span className="fd-modal-summary-label" style={{ color: '#70E000' }}>
              Receitas
            </span>
            <span className="fd-modal-summary-value" style={{ color: '#70E000' }}>
              {formatCurrency(totalReceita)}
            </span>
          </div>
          <div className="fd-modal-summary-item">
            <span className="fd-modal-summary-label" style={{ color: '#e88c2a' }}>
              Despesas
            </span>
            <span className="fd-modal-summary-value" style={{ color: '#e88c2a' }}>
              {formatCurrency(totalDespesa)}
            </span>
          </div>
          <div className="fd-modal-summary-item fd-modal-summary-item--last">
            <span
              className="fd-modal-summary-label"
              style={{ color: resultado >= 0 ? '#70E000' : '#e84040' }}
            >
              Resultado
            </span>
            <span
              className="fd-modal-summary-value"
              style={{ color: resultado >= 0 ? '#70E000' : '#e84040' }}
            >
              {formatCurrency(resultado)}
            </span>
          </div>
        </div>

        <div className="fd-modal-table-wrapper">
          <table className="fd-modal-table fd-modal-table--clickable">
            <thead>
              <tr>
                <SortableTh label="Emissão" sortKey="emissao" sort={sort} onSort={toggleSort} style={{ width: 100 }} />
                <SortableTh label="Tipo" sortKey="tipo" sort={sort} onSort={toggleSort} style={{ width: 90 }} />
                <SortableTh label="Fornecedor / Cliente" sortKey="parte" sort={sort} onSort={toggleSort} />
                <SortableTh label="Plano de Contas" sortKey="plano" sort={sort} onSort={toggleSort} />
                <SortableTh label="Valor" sortKey="valor" sort={sort} onSort={toggleSort} style={{ width: 130, textAlign: 'right' }} />
                <SortableTh label="Parcelas" sortKey="parcelas" sort={sort} onSort={toggleSort} style={{ width: 110, textAlign: 'center' }} />
              </tr>
            </thead>
            <tbody>
              {sorted.map((entry) => {
                const installments = entry.Installment || [];
                const paid = installments.filter(
                  (i) => i.status === INSTALLMENT_STATUS.PAGO
                ).length;
                const total = installments.length;
                return (
                  <tr
                    key={entry.id}
                    onClick={() => handleRowClick(entry)}
                    title="Clique para editar"
                  >
                    <td>{formatDateBR(entry.issue_date)}</td>
                    <td>
                      <span
                        style={{
                          ...typeBadgeStyle(entry.type),
                          padding: '2px 8px',
                          borderRadius: 3,
                          fontSize: 10,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                        }}
                      >
                        {entry.type}
                      </span>
                    </td>
                    <td>{entry.supplier_client || '—'}</td>
                    <td className="fd-table-muted">
                      {entry.AccountPlan?.name || '—'}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>
                      {formatCurrency(entry.total_value)}
                    </td>
                    <td className="fd-table-muted" style={{ textAlign: 'center' }}>
                      {paid}/{total}
                    </td>
                  </tr>
                );
              })}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="fd-table-empty">
                    Nenhum lançamento no período
                  </td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan={6} className="fd-table-empty">
                    Carregando...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </FinancialListModal>

      <FinancialEntryModal
        isOpen={createOpen || editEntryId != null}
        entryId={editEntryId}
        onClose={handleEntryClosed}
        onSaved={handleEntrySaved}
        onDelete={editEntryId != null ? handleDelete : undefined}
      />
    </>
  );
};

export default FinancialEntriesListModal;
